import React, { useEffect, useMemo } from "react";

import { FormStepProps } from "../../../screens/ReviewFormScreen";

import useTranslations from "../../../hooks/useTranslations";

import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer,
  VStack,
} from "@chakra-ui/react";

import { uniq } from "lodash";

import useScheduleAsWords from "../../../hooks/useScheduleAsWords";
import UserAvatarGroup from "../../utils/UserAvatarGroup";
import InlineItem from "../../utils/InlineItem";
import { useGetCampaignLazyQuery, useGetUsersMinimalLazyQuery } from '../../../graphql/generated/definitions';

const ReviewSummaryModal: React.FC<
  Omit<FormStepProps, "trigger" | "onNext"> & {
    isOpen: boolean;
    onClose: () => void;
    loading: boolean;
  }
> = ({ isOpen, onClose, loading, getValues }) => {
  const t = useTranslations();
  const [getUsers, { data: usersData, loading: usersLoading }] =
    useGetUsersMinimalLazyQuery();
  const [getCampaign, { data: campaignData, loading: campaignLoading }] =
    useGetCampaignLazyQuery();

  const formState = useMemo(() => {
    return getValues();
  }, [isOpen]);

  const schedule = useScheduleAsWords({
    schedule: formState?.schedule,
    endDate: formState?.endDate,
    startDate: formState?.startDate,
  });

  useEffect(() => {
    if (!isOpen) return;
    const state = getValues();

    const usersIds: string[] = uniq([
      ...(state?.collaboratorIds || []),
      ...(state?.referentIds || []),
      ...(state?.reviewSupervisorsAttributes?.map(({ userId }) => userId) ||
        []),
    ]);

    if (usersIds.length > 0 && state.campaignId) {
      getUsers({ variables: { ids: usersIds } });
      getCampaign({ variables: { id: state.campaignId } });
    }
  }, [isOpen]);

  const reviewSummary = useMemo(() => {
    if (!usersData || !campaignData) return null;

    const usersInvolved = usersData.users.collection;
    const formState = getValues();

    return {
      schedule,
      campaign: campaignData.campaign,
      collaborators: usersInvolved.filter(({ id }) =>
        formState.collaboratorIds.includes(id)    
      ),
      supervisors: usersInvolved.filter(
        ({ id }) =>
          !!formState.reviewSupervisorsAttributes.find(
            ({ userId }) => userId === id
          )
      ),
      referents: usersInvolved.filter(({ id }) =>
        formState.referentIds.includes(id)
      ),
    };
  }, [usersData, campaignData, schedule]);

  return (
    <Modal isOpen={isOpen} size="xl" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t("reviews.aboutToLaunch", {
            count: reviewSummary?.collaborators.length || 1,
          })}
        </ModalHeader>
        <ModalCloseButton />
        {reviewSummary && !usersLoading && !campaignLoading && (
          <ModalBody>
            <VStack align="stretch">
              <InlineItem
                title={t("reviews.campaign") as string}
                id={reviewSummary.campaign.id}
              >
                {reviewSummary.campaign.name}
              </InlineItem>

              <Spacer />

              <InlineItem
                title={t("reviews.supervisors") as string}
                id={`supervisors-${reviewSummary.supervisors
                  .map(({ id }) => id)
                  .sort()
                  .join("-")}`}
              >
                <UserAvatarGroup users={reviewSummary.supervisors} />
              </InlineItem>

              <Spacer />

              <InlineItem
                title={t("reviews.collaborators") as string}
                id={`collaborators-${reviewSummary.collaborators
                  .map(({ id }) => id)
                  .sort()
                  .join("-")}`}
              >
                <UserAvatarGroup users={reviewSummary.collaborators} />
              </InlineItem>

              <Spacer />

              <InlineItem
                title={t("reviews.referents") as string}
                id={`referents-${reviewSummary.referents
                  .map(({ id }) => id)
                  .sort()
                  .join("-")}`}
              >
                {reviewSummary.referents.length > 0 ? (
                  <UserAvatarGroup users={reviewSummary.referents} />
                ) : (
                  t("reviews.noReferent")
                )}
              </InlineItem>

              <Spacer />

              <InlineItem
                id="frequency"
                title={t("reviews.reportsFrequencyTitle") as string}
              >
                {reviewSummary.schedule}
              </InlineItem>
            </VStack>
          </ModalBody>
        )}
        <ModalFooter>
          <HStack>
            <Button onClick={onClose}>{t("translations.back")}</Button>

            <Spacer />

            <Button
              type="submit"
              form="reviewCreationForm"
              isLoading={loading}
              colorScheme="blue"
            >
              {t("reviews.actions.send")}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(ReviewSummaryModal);
